import {AfterViewInit, ChangeDetectorRef, Component, DoCheck, OnInit ,ElementRef, EventEmitter, Input, Renderer2, ViewChild, ViewChildren, QueryList} from '@angular/core';
import { ControlValueAccessor, NgControl, Validators, ValidatorFn , AbstractControl} from "@angular/forms";
import {InputDropdownComponent} from "../input-dropdown.component";
import {lastValueFrom} from "rxjs";
import {ValgOptionInput, OptionPostition} from "../../input.types";
import {TranslateService} from "@ngx-translate/core";
import {ScrollStrategyOptions} from "@angular/cdk/overlay";
import { SvgIcon } from '@valg-shared/utils/svg-icon';
import { FilterPipe } from '../filter.pipe';
import { LocalStorageService } from '@valg-shared/services/local-storage-service/local-storage.service';


@Component({
  selector: 'valg-combo-box',
  templateUrl: './combo-box.component.html'
})
export class ComboBoxComponent<T = string | number> extends InputDropdownComponent<T> implements ControlValueAccessor, AfterViewInit, DoCheck, OnInit {
  @Input() includeOtherOption = false;
  @Input() secondaryLabel?: string;
  @Input() secondaryPlaceholder?: string;
  @Input() secondaryInputRequired = false;
  @Input() enableNotInListValidator = false;
  @Input() allowCustomValues = false;
  @Input() keyUpEvent: EventEmitter<string> = new EventEmitter<string>();
  @Input() externalAutoComplete = false;
  @Input() displayValueToControlValueConverter: (val: string) => T | undefined = (val: string) => val as unknown as T
  @Input() controlValueToDisplayValueConverter: (controlValue?: T) => string | undefined = (controlValue?: T) => controlValue as unknown as string
  @Input() fieldIcon!: SvgIcon;

  private readonly _otherOption = { label: 'standard_input.common.other', isOtherOption: true, isAlwaysOn: OptionPostition.Top };

  _secondaryValue?: string;
  _primaryValue: string = "";
  @ViewChild('mainInput') public readonly _mainInput!: ElementRef;
  @ViewChildren('allOptions') _allOptions!: QueryList<ElementRef>;

  constructor(protected override readonly _translateService: TranslateService,
    protected override readonly _sso: ScrollStrategyOptions,
    protected override readonly cdr: ChangeDetectorRef,
    renderer: Renderer2,
    hostElement: ElementRef,
    private readonly filterPipe: FilterPipe,
    localStorageService: LocalStorageService,
    ngControl: NgControl) {
      super(_translateService, _sso, renderer, hostElement, ngControl, cdr, localStorageService);
      this.setValueOnTab = false;
  }

  override ngAfterViewInit() {
    super.ngAfterViewInit();
    this.setMainInputValue(this.controlValueToDisplayValueConverter(this._controlValue));
    if (this.secondaryInputRequired) {
      this.toggleRequiredValidatorOnOtherOptionSelected()
    }

    this.updatePositionOnOptionsChange();
  }

  onFocus() {
    this.isMenuOverlayTriggered = true;
    this.openDropdownMenu();
  }

  dropDownArrowClicked(e: Event) {
    e.stopPropagation()
    if (!this.isMenuOverlayTriggered) {
      this.isMenuOverlayTriggered = true;
      this.openDropdownMenu();
    } else {
      this.isMenuOverlayTriggered = false;
    }
  }

  ngOnInit() {
    if(this.enableNotInListValidator){
      this.formControl.control?.addValidators(this.notInListValidator())
    }
  }

  onOtherInputBlur(){
    this.onTouched();
    this.onBlurEvent.emit();
  }

  onBlur() {
    this.ngControl.control?.updateValueAndValidity();
  }

  override async ngDoCheck() {
    if (this.isOptionsChange()) {
      if(this.includeOtherOption) {
        await this.addOtherOption()
      }
    }
    super.ngDoCheck();
  }

  onKeyUp(event: KeyboardEvent) {
    if(this.isNotDropdownNavigationKey(event.code)) {
      const value = this._mainInput.nativeElement.value;
      this.onTouched();
      if (this.allowCustomValues && event.code != "Enter") {
        this.onChange(this.displayValueToControlValueConverter(value));
      }
      this.keyUpEvent.emit(value)
      this._primaryValue = this._mainInput.nativeElement.value;
      this.isMenuOverlayTriggered = true;
    }
  }

  override onKeyDown(event: KeyboardEvent) {
    if(this.isNotDropdownNavigationKey(event.code)) {
      this.activeIndex = null;
    }
    super.onKeyDown(event);
    if(event.code === 'Enter') {
      this.closeDropdownMenu();
    }
  }
  
  protected override getFilteredOptions() {
    return this.filterPipe.transform(this.options, "label", this._primaryValue)
  }

  updatePositionOnOptionsChange(): void{
    this._allOptions.changes.subscribe(() => {
      if(this.isMenuVisible){
        this.overlayDir.overlayRef.updatePosition();
      }
    });
  }

  override selectItem(option: ValgOptionInput<T>, isFromUserAction: boolean = false) {
    super.selectItem(option, isFromUserAction);
    this._primaryValue = option.label;
    if(this._emptyOption.label == this._primaryValue){
      this._primaryValue = "";
    }

    if (this.externalAutoComplete) {
      this.keyUpEvent.emit(option.label);
    }
    
    if (option.isOtherOption && this._secondaryValue) {
      this.onChange(this._secondaryValue);
    }
  }
  
  override async onItemClicked(event: Event, option: ValgOptionInput<T>){
    this._primaryValue = option.label;
    await super.onItemClicked(event, option);
    if(!option.isOtherOption){
      this.dropdownTrigger?.nativeElement.focus();
    }
    this.closeDropdownMenu();
  }

  selectOptionIfMatch(){
    let value = this.formatToCompare(this._primaryValue);
    if(value !== ""){
      let options = this.options.filter(option => {
        return this.formatToCompare(option['label']) === value;
      });
      if(options.length === 1){
        let option = options[0];
        let optionIndex = this.getFilteredOptions().indexOf(option);
        this.keyManager.updateActiveItem(optionIndex);
        this.activeIndex = optionIndex;
        this.cdr.detectChanges();
        this.selectItem(option, true);

      }
      
    }
  }

  formatToCompare(value: string) : string{
    return value.trim().toLowerCase().replace(/\s/g,'')
  }

  override closeDropdownMenu() {
    this.selectOptionIfMatch();
    this.removeOutsideClickListener();
    this.isMenuVisible = false;
    this.dropdownMenuClose.emit();
    if(!this.selectedOption?.isOtherOption){
      this.onTouched();
      this.onBlurEvent.emit();
    } else {
      this.otherOptionInput?.nativeElement.focus();
    }

    this.formControl.control?.updateValueAndValidity();
  }


  private notInListValidator(): ValidatorFn {
    return (_control: AbstractControl): { [key: string]: any } | null => {
      const inputNotInList = this._primaryValue !== "" && this.options.find(option => option.label === this._primaryValue) === undefined;
      if(inputNotInList){
        return { 'not_in_list': true };
      }
      return null;
    };
  }

  override updateValue() {
    if (!this.allowCustomValues) {
      super.updateValue();
    }
  }

  override handleControlValueChange(val?: T) {
    const strVal = this.controlValueToDisplayValueConverter(val)
    let selectedOption;
    if (val === undefined || val === null){
      selectedOption = undefined;
      this.setMainInputValue("")
    } else if (this.options?.length > 0) {
      if (this.includeOtherOption && strVal && !this.isValueIncludedInOptionList(val)) {
        selectedOption = this.options.find(option => option.isOtherOption === true)
        this._secondaryValue = strVal;
      }
      else {
        selectedOption = this.options.find(option => option?.value == val);
      }
    }
    if (this.allowCustomValues) {
      this.setMainInputValue(strVal);
    } else {
      this.updateDisplayValueOnChange(selectedOption)
    }
  }

  override updateDisplayValueOnChange(option?: ValgOptionInput<T> ) {
    super.updateDisplayValueOnChange(option);
    if(option) {
      if(this._mainInput){
        let label = '';
        if (this.allowCustomValues || !option.isEmptyOption) {
            label = option.label;
        }
        this.setMainInputValue(label);
        this.cdr.detectChanges();
        this.changeEvent.emit(option);
      }
    }
    if (this.allowCustomValues) {
      this.formControl.control?.updateValueAndValidity();
    }
  }

  onSecondaryInputChange(eventTarget: EventTarget | null) {
    const secondaryInput = eventTarget as HTMLInputElement
    this._secondaryValue = secondaryInput.value;
    this.onChange(secondaryInput.value)
    this.onTouched();
  }

  private async addOtherOption() {
    this._otherOption.label = await lastValueFrom(this._translateService.get(this._otherOption.label))
    if(!this.options.find(option => option.isOtherOption)) {
        this.options.push(this._otherOption);
      }
    }
  
  private isValueIncludedInOptionList(val: T) {
    return this.options.find(option => option.value === val) != undefined;
  }
  

  private toggleRequiredValidatorOnOtherOptionSelected() {
    this.changeEvent.subscribe((value: ValgOptionInput<string>) => {
      if (value?.isOtherOption) {
        if (!this.ngControl.control?.hasValidator(Validators.required)) {
          this.ngControl.control?.addValidators(Validators.required)
        }
      } else {
        if (this.ngControl.control?.hasValidator(Validators.required)) {
          this.ngControl.control?.removeValidators(Validators.required)
        }
      }
      this.ngControl.control?.updateValueAndValidity();
    })
  }

  private isNotDropdownNavigationKey(code: string) {
    return code !== 'Escape' && code !== 'Tab' && code !== 'ArrowUp' && code !== 'ArrowDown'
  }

  private setMainInputValue(val?: string) {
    if (this._mainInput) {
      this._mainInput.nativeElement.value = val ?? "";
      this._primaryValue = this._mainInput.nativeElement.value;
    }
  }
}